import type { Product } from '../services/api/product.service';

export type PrintSide = 'front' | 'back';

export type ProductPrintType =
  | 'tshirt'
  | 'hoodie'
  | 'sweatshirt'
  | 'tote_bag'
  | 'cap'
  | 'mug';

export interface PrintTemplateSide {
  id: PrintSide;
  label: string;
  physicalWidthIn: number;
  physicalHeightIn: number;
  logicalWidth: number;
  logicalHeight: number;
  safeZone: {
    marginPx: number;
    marginIn: number;
  };
  bleedPx: number;
  exportMultiplier: number;
  targetDpi: number;
}

export interface ProductPrintTemplate {
  id: ProductPrintType;
  label: string;
  productType: ProductPrintType;
  sides: Partial<Record<PrintSide, PrintTemplateSide>>;
  defaultSide: PrintSide;
}

const side = (
  id: PrintSide,
  label: string,
  physicalWidthIn: number,
  physicalHeightIn: number,
): PrintTemplateSide => {
  const aspect = physicalWidthIn / physicalHeightIn;
  const maxVisibleLongSide = 480;
  const maxVisibleShortSide = 480;
  const logicalHeight =
    aspect >= 1
      ? Math.round(maxVisibleShortSide / aspect)
      : maxVisibleLongSide;
  const logicalWidth =
    aspect >= 1
      ? maxVisibleLongSide
      : Math.round(maxVisibleLongSide * aspect);
  const exportMultiplier = Math.max(
    physicalWidthIn * 300 / logicalWidth,
    physicalHeightIn * 300 / logicalHeight,
  );
  return {
    id,
    label,
    physicalWidthIn,
    physicalHeightIn,
    logicalWidth,
    logicalHeight,
    safeZone: {
      marginPx: Math.round(Math.min(logicalWidth, logicalHeight) * 0.06),
      marginIn: 0.35,
    },
    bleedPx: Math.round(Math.min(logicalWidth, logicalHeight) * 0.025),
    exportMultiplier,
    targetDpi: 300,
  };
};

export const PRINT_TEMPLATES: Record<ProductPrintType, ProductPrintTemplate> = {
  tshirt: {
    id: 'tshirt',
    label: 'T-shirt',
    productType: 'tshirt',
    defaultSide: 'front',
    sides: {
      front: side('front', 'Front', 12, 16),
      back: side('back', 'Back', 12, 16),
    },
  },
  hoodie: {
    id: 'hoodie',
    label: 'Hoodie',
    productType: 'hoodie',
    defaultSide: 'front',
    sides: {
      front: side('front', 'Front', 12, 14),
      back: side('back', 'Back', 12, 14),
    },
  },
  sweatshirt: {
    id: 'sweatshirt',
    label: 'Sweatshirt',
    productType: 'sweatshirt',
    defaultSide: 'front',
    sides: {
      front: side('front', 'Front', 12, 14),
      back: side('back', 'Back', 12, 14),
    },
  },
  tote_bag: {
    id: 'tote_bag',
    label: 'Tote bag',
    productType: 'tote_bag',
    defaultSide: 'front',
    sides: {
      front: side('front', 'Front', 10, 12),
    },
  },
  cap: {
    id: 'cap',
    label: 'Cap',
    productType: 'cap',
    defaultSide: 'front',
    sides: {
      front: side('front', 'Front', 4, 2),
    },
  },
  mug: {
    id: 'mug',
    label: 'Mug',
    productType: 'mug',
    defaultSide: 'front',
    sides: {
      front: side('front', 'Wrap', 8.5, 3),
    },
  },
};

export const DEFAULT_PRINT_TEMPLATE = PRINT_TEMPLATES.tshirt;

export function getPrintTemplate(productType?: string | null): ProductPrintTemplate {
  const normalized = normalizeProductPrintType(productType);
  return PRINT_TEMPLATES[normalized] ?? DEFAULT_PRINT_TEMPLATE;
}

export function getTemplateSides(template: ProductPrintTemplate): PrintSide[] {
  return (['front', 'back'] as PrintSide[]).filter((sideId) => Boolean(template.sides[sideId]));
}

export function getTemplateSide(
  template: ProductPrintTemplate,
  requestedSide?: PrintSide | string | null,
): PrintTemplateSide {
  const sideId = requestedSide === 'back' && template.sides.back ? 'back' : template.defaultSide;
  return template.sides[sideId] ?? template.sides.front ?? DEFAULT_PRINT_TEMPLATE.sides.front!;
}

export function normalizeProductPrintType(value?: string | null): ProductPrintType {
  const raw = String(value ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (raw.includes('mug')) return 'mug';
  if (raw.includes('hoodie')) return 'hoodie';
  if (raw.includes('sweat')) return 'sweatshirt';
  if (raw.includes('tote') || raw.includes('bag')) return 'tote_bag';
  if (raw.includes('cap') || raw.includes('hat')) return 'cap';
  return 'tshirt';
}

export function resolveProductPrintType(product?: Product | null): ProductPrintType {
  if (!product) return DEFAULT_PRINT_TEMPLATE.productType;
  return normalizeProductPrintType(
    product.metadata?.productType ||
      product.category ||
      product.name ||
      product.slug,
  );
}

export function resolveProductPrintTemplate(product?: Product | null): ProductPrintTemplate {
  return getPrintTemplate(resolveProductPrintType(product));
}
